import { useRef, useEffect } from 'react';

/**
 * Custom hook for PersonalisationHelperModal logic and className selection.
 *
 * @param {Object} props - Hook props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback for closing the modal
 * @returns {{ dialogRef: React.RefObject<HTMLDialogElement | null>, handleClose: () => void }}
 */
export function useViewModel({ isOpen, onClose }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      setTimeout(() => {
        dialog.setAttribute('data-open', 'true');
      }, 10);
    } else if (!isOpen && dialog.open) {
      dialog.setAttribute('data-open', 'false');
      setTimeout(() => {
        dialog.close();
      }, 400);
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      if (!isOpen) return;
      if (isOpen) {
        dialog.setAttribute('data-open', 'false');
        setTimeout(() => {
          onClose?.();
        }, 400);
      }
    };

    dialog.addEventListener('close', handleNativeClose);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, [isOpen, onClose]);

  /**
   * Handles closing the modal dialog.
   * @returns {void}
   */
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return {
    dialogRef,
    handleClose
  };
}
